import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { ConnectedCard } from "@/domains/settings/features/integrations/google-business/components/connected-card";
import { ConnectionStatusWidget } from "@/domains/settings/features/integrations/google-business/components/connection-status-widget";

interface ConnectionRow {
  locationName: string;
  connectedAt: string;
  authMode: "OWNER_OAUTH" | "MANAGED_PLATFORM";
  managerEmail?: string | null;
  reauthRequired: boolean;
  lastLocationSyncAt?: string | null;
  lastLocationSyncError?: string | null;
  otherSalonsUsingSameAccount?: { salonId: string; salonName: string }[] | null;
}

interface HealthSummary {
  hasConnection: boolean;
  authMode?: "OWNER_OAUTH" | "MANAGED_PLATFORM" | null;
  managerEmail?: string | null;
  lastSyncAt?: string | null;
  lastSyncError?: string | null;
  nextScheduledSyncAt?: string | null;
  syncEnabled: boolean;
  reauthRequired: boolean;
  managerReauthRequired: boolean;
  managerActive: boolean;
}

interface GoogleConnectionSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  connection: ConnectionRow;
  health: HealthSummary | null;
  onDisconnect: () => void;
  disconnecting: boolean;
  onReconnect: () => void;
  reconnecting: boolean;
}

export function GoogleConnectionSheet({
  open,
  onOpenChange,
  connection,
  health,
  onDisconnect,
  disconnecting,
  onReconnect,
  reconnecting,
}: GoogleConnectionSheetProps) {
  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="w-full sm:max-w-lg overflow-y-auto">
        <SheetHeader>
          <SheetTitle>Google Business connection</SheetTitle>
          <SheetDescription>Connection details and sync history for this salon.</SheetDescription>
        </SheetHeader>

        <div className="mt-6 space-y-4">
          <ConnectedCard
            locationName={connection.locationName}
            connectedAt={connection.connectedAt}
            authMode={connection.authMode}
            managerEmail={connection.managerEmail}
            otherSalonsUsingSameAccount={connection.otherSalonsUsingSameAccount ?? []}
            lastLocationSyncAt={connection.lastLocationSyncAt}
            lastLocationSyncError={connection.lastLocationSyncError}
            onDisconnect={onDisconnect}
            isLoading={disconnecting}
          />
          {health ? (
            <ConnectionStatusWidget
              health={health}
              onReconnect={onReconnect}
              reconnecting={reconnecting}
            />
          ) : null}
        </div>
      </SheetContent>
    </Sheet>
  );
}
